import AsyncStorage from "@react-native-async-storage/async-storage";
import { Platform } from "expo-modules-core";
import * as SecureStore from "expo-secure-store";
import { parse, stringify } from "superjson";

export interface StateStorage<TKey extends string> {
    getItem(name: TKey): Promise<string | null>;
    setItem(name: TKey, value: string): Promise<void>;
    removeItem(name: TKey): Promise<void>;
}

export const SecureStorage: StateStorage<string> = {
    getItem: (name: string): Promise<string | null> => SecureStore.getItemAsync(name),
    setItem: (name: string, value: string): Promise<void> => SecureStore.setItemAsync(name, value),
    removeItem: (name: string): Promise<void> => SecureStore.deleteItemAsync(name),
};

export const makePlatformStorage = <TKey extends string>(): StateStorage<TKey> =>
    Platform.select({
        ios: SecureStorage,
        android: SecureStorage,
        default: AsyncStorage,
    });

export class SuperJsonStorage<
    TKey extends string,
    TPrefix extends string,
    TEngine extends StateStorage<`${TPrefix}${TKey}`> = StateStorage<`${TPrefix}${TKey}`>,
> {
    constructor(
        private engine: TEngine,
        readonly prefix: TPrefix,
    ) {
        //
    }

    async getItem<TValue>(name: TKey): Promise<TValue | null> {
        const deserialize = (str: string | null | undefined): TValue | null => {
            if (typeof str !== "string") {
                return null;
            }

            return parse(str);
        };

        return deserialize((await this.engine.getItem(`${this.prefix}${name}`)) ?? null);
    }
    async removeItem(name: TKey) {
        await this.engine.removeItem(`${this.prefix}${name}`);
    }
    async setItem<TValue>(name: TKey, value?: TValue | null) {
        // Allow falsy but not nullish values
        if (value === (value ?? "nullish")) {
            await this.engine.setItem(`${this.prefix}${name}`, stringify(value));
        } else {
            await this.removeItem(name);
        }
    }
}

export const makeSecureStorage = <TKey extends string, TPrefix extends string = string>(
    prefix: TPrefix,
): SuperJsonStorage<TKey, TPrefix> => {
    return new SuperJsonStorage(makePlatformStorage(), prefix);
};

export enum AuthKey {
    level = "security_level",
    token = "token",
    passcode = "passcode",
}
export enum ProfileKey {
    profile = "profile",
}
export enum UserKey {
    user = "user",
    teams = "teams",
}
export enum NotificationKey {
    notification_settings = "notification_settings",
    notifications = "notifications",
}
export enum OnboardingKey {
    onboarded = "onboarded",
}
export enum PreferenceKey {
    theme = "theme",
}

export const authStorage = makeSecureStorage<AuthKey>("_auth__");
export const profileStorage = makeSecureStorage<ProfileKey>("_profile__");
export const userStorage = makeSecureStorage<UserKey>("_user__");
export const notificationStorage: SuperJsonStorage<NotificationKey, "_notify__"> = new SuperJsonStorage(
    AsyncStorage,
    "_notify__",
);
export const onboardingStorage: SuperJsonStorage<OnboardingKey, "_onboarding__"> = new SuperJsonStorage(
    AsyncStorage,
    "_onboarding__",
);
export const preferenceStorage: SuperJsonStorage<PreferenceKey, "_preference__"> = new SuperJsonStorage(
    AsyncStorage,
    "_preference__",
);

export const purgeStorage = async () => {
    await Promise.allSettled([
        ...Object.values(AuthKey).map((key) => authStorage.removeItem(key)),
        ...Object.values(ProfileKey).map((key) => profileStorage.removeItem(key)),
        ...Object.values(UserKey).map((key) => userStorage.removeItem(key)),
        ...Object.values(NotificationKey).map((key) => notificationStorage.removeItem(key)),
        ...Object.values(OnboardingKey).map((key) => onboardingStorage.removeItem(key)),
    ]);
};
